/**
 * GENERATED_ASSET — revision-02 input guard (TF-04/TF-05).
 * Field-path diagnostics for input-facing numeric APIs. Never coerces: "12", true, null and NaN are all rejected.
 * Leaf module (no sibling imports) so it drops into the destination directory unchanged.
 */
export interface InputIssue { readonly path: string; readonly message: string }

export class InputValidationError extends RangeError {
  constructor(readonly context: string, readonly issues: readonly InputIssue[]) {
    super(`Invalid ${context} (${issues.length} problem${issues.length === 1 ? '' : 's'}):\n`
      + issues.map((i) => ` - ${i.path}: ${i.message}`).join('\n'));
    this.name = 'InputValidationError';
  }
}

/** Short, unambiguous description of a bad value for messages (type + value, no coercion). */
export function describeValue(v: unknown): string {
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return `string ${JSON.stringify(v.length > 24 ? `${v.slice(0, 24)}...` : v)}`;
  if (v === null) return 'null';
  if (Array.isArray(v)) return `array(${v.length})`;
  return typeof v;
}

export const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
export const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

export function finite(issues: InputIssue[], path: string, v: unknown): void {
  if (!isFiniteNumber(v)) issues.push({ path, message: `must be a finite number (got ${describeValue(v)})` });
}

/** Wording keeps the phrase "scale must be a positive finite number" that candidate1's checks match. */
export function positiveScale(issues: InputIssue[], path: string, v: unknown): void {
  if (!isFiniteNumber(v) || v <= 0) issues.push({ path, message: `scale must be a positive finite number (got ${describeValue(v)})` });
}

export function point(issues: InputIssue[], path: string, p: unknown): void {
  if (!isRecord(p)) { issues.push({ path, message: `must be an object with finite x and y (got ${describeValue(p)})` }); return; }
  finite(issues, `${path}.x`, p.x);
  finite(issues, `${path}.y`, p.y);
}

export function optionalBoolean(issues: InputIssue[], path: string, v: unknown): void {
  if (v !== undefined && typeof v !== 'boolean') issues.push({ path, message: `must be a boolean or omitted (got ${describeValue(v)})` });
}

export function matrix(issues: InputIssue[], path: string, m: unknown): void {
  if (!Array.isArray(m) || m.length !== 6) { issues.push({ path, message: `must be an array of 6 finite numbers (got ${describeValue(m)})` }); return; }
  for (let i = 0; i < 6; i++) finite(issues, `${path}[${i}]`, m[i]);
}

export function bounds(issues: InputIssue[], path: string, b: unknown): void {
  if (!isRecord(b)) { issues.push({ path, message: `must be an object {x,y,width,height} (got ${describeValue(b)})` }); return; }
  for (const k of ['x', 'y', 'width', 'height'] as const) finite(issues, `${path}.${k}`, b[k]);
  for (const k of ['width', 'height'] as const) {
    if (isFiniteNumber(b[k]) && (b[k] as number) < 0) issues.push({ path: `${path}.${k}`, message: `must be >= 0 (got ${b[k]})` });
  }
}

export function throwIfIssues(context: string, issues: readonly InputIssue[]): void {
  if (issues.length) throw new InputValidationError(context, issues);
}

/** SVG exponent notation preserves huge finite values; the rounding multiply must never overflow. */
export function finiteSvgNumber(value: number, path = 'svg.number'): string {
  const issues: InputIssue[] = [];
  finite(issues, path, value); throwIfIssues('SVG number', issues);
  return String(Math.abs(value) > Number.MAX_VALUE / 1e6 ? value : Math.round(value * 1e6) / 1e6);
}
